import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Target, Plus, Loader2, Search, ChevronLeft, Trash2, RefreshCw,
  UserPlus, CheckCircle2, XCircle, Send, FolderSearch, ClipboardList, AlertTriangle,
  Building2, Mail, ShieldAlert, Globe, Sparkles,
} from "lucide-react";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const TARGET_TYPES = ["executive", "hr", "finance", "operations", "legal", "sales", "it", "marketing"];

const STATUS_BADGE = {
  pending: { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Not searched" },
  searching: { cls: "bg-blue-100 text-blue-700 border-blue-200", label: "Searching" },
  ready_for_approval: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Contacts found" },
  no_email_found: { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "No email found" },
  search_failed: { cls: "bg-rose-100 text-rose-700 border-rose-200", label: "Search failed" },
  excluded: { cls: "bg-slate-200 text-slate-600 border-slate-300", label: "Restricted" },
};

const REVIEW_BADGE = {
  discovered: { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Discovered" },
  approved: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Approved" },
  rejected: { cls: "bg-rose-100 text-rose-700 border-rose-200", label: "Rejected" },
  enrolled: { cls: "bg-violet-100 text-violet-700 border-violet-200", label: "Enrolled" },
};

const req = (url, opts = {}) =>
  fetch(`${API}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });

function CoverageBar({ coverage }) {
  if (!coverage) return null;
  const oc = coverage.organisation_coverage || {};
  const cr = coverage.contact_review || {};
  const rd = coverage.recipient_delivery || {};
  const Stat = ({ label, value, cls, tid }) => (
    <div className="text-center px-3" data-testid={tid}>
      <div className={`text-2xl font-bold ${cls}`}>{value || 0}</div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  );
  return (
    <Card className="border-slate-200" data-testid="coverage-panel">
      <CardContent className="py-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" /> Organisation coverage
            </div>
            <div className="flex flex-wrap">
              <Stat label="Targets" value={oc.total} cls="text-slate-900" tid="cov-total" />
              <Stat label="Found" value={oc.ready_for_approval} cls="text-emerald-600" tid="cov-found" />
              <Stat label="No email" value={oc.no_email_found} cls="text-amber-600" tid="cov-noemail" />
              <Stat label="Failed" value={oc.search_failed} cls="text-rose-600" tid="cov-failed" />
              <Stat label="Resolved" value={oc.resolved} cls="text-blue-600" tid="cov-resolved" />
            </div>
          </div>
          <div className="md:border-l border-slate-200 md:pl-4">
            <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Contact review
            </div>
            <div className="flex flex-wrap">
              <Stat label="Approved" value={cr.approved} cls="text-emerald-600" tid="cov-approved" />
              <Stat label="Enrolled" value={cr.enrolled} cls="text-violet-600" tid="cov-enrolled" />
            </div>
          </div>
          <div className="md:border-l border-slate-200 md:pl-4">
            <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <Send className="w-3.5 h-3.5" /> Recipient delivery
            </div>
            <div className="flex flex-wrap">
              <Stat label="Draft" value={rd.pending} cls="text-slate-900" tid="cov-queued" />
              <Stat label="Sent" value={rd.sent} cls="text-emerald-600" tid="cov-sent" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OrgRow({ list, org, onChange, onToggleContact, selected }) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manual, setManual] = useState({ name: "", email: "", title: "" });
  const [showManual, setShowManual] = useState(false);
  const badge = STATUS_BADGE[org.discovery_status] || STATUS_BADGE.pending;

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const d = await req(`/target-lists/${list.target_list_id}/orgs/${org.target_org_id}/contacts`).then((r) => r.json());
      setContacts(d.contacts || []);
    } catch { toast.error("Failed to load contacts"); }
    finally { setLoading(false); }
  }, [list.target_list_id, org.target_org_id]);

  const toggle = () => { const n = !open; setOpen(n); if (n && contacts === null) loadContacts(); };

  const discoverOne = async (e) => {
    e.stopPropagation();
    try {
      await req(`/target-lists/${list.target_list_id}/orgs/${org.target_org_id}/discover`, { method: "POST", body: "{}" });
      toast.success("Search complete"); onChange(); if (open) loadContacts();
    } catch { toast.error("Search failed"); }
  };

  const decide = async (cid, action) => {
    try {
      await req(`/target-lists/${list.target_list_id}/contacts/${cid}/${action}`, { method: "POST", body: "{}" });
      loadContacts(); onChange();
    } catch { toast.error(`Could not ${action}`); }
  };

  const addManual = async () => {
    if (!manual.name || !manual.email) { toast.error("Name and email required"); return; }
    try {
      await req(`/target-lists/${list.target_list_id}/orgs/${org.target_org_id}/contacts`, {
        method: "POST", body: JSON.stringify(manual),
      }).then((r) => { if (!r.ok) throw new Error(); });
      toast.success("Contact added"); setManual({ name: "", email: "", title: "" }); setShowManual(false);
      loadContacts(); onChange();
    } catch { toast.error("Could not add contact"); }
  };

  return (
    <div className="border border-slate-200 rounded-lg" data-testid={`org-row-${org.target_org_id}`}>
      <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50" onClick={toggle}>
        <div className="flex items-center gap-3">
          <Building2 className="w-4 h-4 text-slate-400" />
          <div>
            <div className="font-medium text-slate-900">{org.company_name}</div>
            <div className="text-xs text-slate-500">{org.domain || "no domain"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={badge.cls} data-testid={`org-status-${org.target_org_id}`}>{badge.label}</Badge>
          {!org.blocked && (
            <Button size="sm" variant="ghost" onClick={discoverOne} data-testid={`org-discover-${org.target_org_id}`}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-100 p-3 space-y-2 bg-slate-50/50">
          {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {!loading && contacts && contacts.length === 0 && (
            <div className="text-sm text-slate-500">No contacts discovered yet.</div>
          )}
          {!loading && contacts && contacts.map((c) => {
            const rb = REVIEW_BADGE[c.review_status] || REVIEW_BADGE.discovered;
            const canSelect = c.review_status === "approved";
            return (
              <div key={c.target_contact_id} className="flex items-center justify-between bg-white border border-slate-200 rounded px-3 py-2" data-testid={`contact-${c.target_contact_id}`}>
                <div className="flex items-center gap-3">
                  {canSelect && (
                    <Checkbox checked={!!selected[c.target_contact_id]} onCheckedChange={() => onToggleContact(c)} data-testid={`select-contact-${c.target_contact_id}`} />
                  )}
                  <div>
                    <div className="text-sm font-medium text-slate-900">{c.name || "(no name)"} <span className="text-xs text-slate-400">{c.title}</span></div>
                    <div className="text-xs text-slate-500">
                      {c.email} · {c.greeting_preview || (c.greeting_hold_reason ? <span className="text-amber-600" title={c.greeting_hold_reason}>greeting held</span> : "—")} ·{" "}
                      <span className={["invalid", "disposable"].includes(c.verify_status) ? "text-rose-600 font-medium" : ""}>
                        {["invalid", "disposable"].includes(c.verify_status) && <AlertTriangle className="w-3 h-3 inline mr-0.5 -mt-0.5" />}{c.verify_status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={rb.cls}>{rb.label}</Badge>
                  {c.review_status !== "enrolled" && (
                    <>
                      <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => decide(c.target_contact_id, "approve")} data-testid={`approve-${c.target_contact_id}`}>
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => decide(c.target_contact_id, "reject")} data-testid={`reject-${c.target_contact_id}`}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {!org.blocked && (
            <div>
              {!showManual ? (
                <Button size="sm" variant="outline" onClick={() => setShowManual(true)} data-testid={`add-manual-${org.target_org_id}`}>
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Add contact by hand
                </Button>
              ) : (
                <div className="bg-white border border-slate-200 rounded p-3 space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Full name" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} data-testid={`manual-name-${org.target_org_id}`} />
                    <Input placeholder="Email" value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} data-testid={`manual-email-${org.target_org_id}`} />
                    <Input placeholder="Title" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} data-testid={`manual-title-${org.target_org_id}`} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={addManual} data-testid={`manual-save-${org.target_org_id}`}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowManual(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InvestigationTab({ list, onChange }) {
  const [orgs, setOrgs] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await req(`/target-lists/${list.target_list_id}/investigation`).then((r) => r.json());
      setOrgs(d.orgs || []);
      setResolved(d.resolved || []);
    } finally { setLoading(false); }
  }, [list.target_list_id]);
  useEffect(() => { load(); }, [load]);

  const save = async (org, extra = {}) => {
    const body = { ...(edit[org.target_org_id] || {}), ...extra };
    try {
      await req(`/target-lists/${list.target_list_id}/orgs/${org.target_org_id}/investigation`, { method: "POST", body: JSON.stringify(body) });
      toast.success("Updated"); setEdit({ ...edit, [org.target_org_id]: {} }); load(); onChange();
    } catch { toast.error("Could not save"); }
  };

  if (loading) return <div className="text-sm text-slate-500 flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (orgs.length === 0 && resolved.length === 0) return <div className="text-sm text-slate-500 py-6">No organisations need investigation. Every target has a contact or is resolved.</div>;

  return (
    <div className="space-y-3" data-testid="investigation-list">
      {orgs.map((org) => {
        const e = edit[org.target_org_id] || {};
        const set = (k, v) => setEdit({ ...edit, [org.target_org_id]: { ...e, [k]: v } });
        return (
          <Card key={org.target_org_id} className="border-amber-200" data-testid={`investigation-org-${org.target_org_id}`}>
            <CardContent className="py-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-medium text-slate-900 flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-amber-500" /> {org.company_name}
                </div>
                <Badge variant="outline" className={(STATUS_BADGE[org.discovery_status] || {}).cls}>
                  {(STATUS_BADGE[org.discovery_status] || {}).label}
                </Badge>
              </div>
              {org.last_search?.detail && <div className="text-xs text-rose-600">{org.last_search.detail}</div>}
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Owner" defaultValue={org.owner || ""} onChange={(ev) => set("owner", ev.target.value)} data-testid={`inv-owner-${org.target_org_id}`} />
                <Input placeholder="Next action" defaultValue={org.next_action || ""} onChange={(ev) => set("next_action", ev.target.value)} data-testid={`inv-next-${org.target_org_id}`} />
              </div>
              <Textarea placeholder="Add a note…" value={e.note || ""} onChange={(ev) => set("note", ev.target.value)} data-testid={`inv-note-${org.target_org_id}`} />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => save(org)} data-testid={`inv-save-${org.target_org_id}`}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => save(org, { resolution_status: "resolved" })} data-testid={`inv-resolve-${org.target_org_id}`}>Mark resolved</Button>
              </div>
              <HistoryBlock org={org} />
            </CardContent>
          </Card>
        );
      })}
      {resolved.length > 0 && (
        <div className="pt-2" data-testid="investigation-resolved">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Resolved — still need a contact</div>
          {resolved.map((org) => (
            <Card key={org.target_org_id} className="border-blue-200 mb-2" data-testid={`investigation-resolved-${org.target_org_id}`}>
              <CardContent className="py-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-900 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" /> {org.company_name}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Resolved</Badge>
                    <Button size="sm" variant="ghost" onClick={() => save(org, { resolution_status: "open" })} data-testid={`inv-reopen-${org.target_org_id}`}>Reopen</Button>
                  </div>
                </div>
                <HistoryBlock org={org} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryBlock({ org }) {
  const notes = org.notes || [];
  const history = org.resolution_history || [];
  if (notes.length === 0 && history.length === 0) return null;
  return (
    <div className="text-xs text-slate-500 space-y-1 border-t border-slate-100 pt-2">
      {notes.map((n, i) => <div key={`n${i}`}>• {n.text} <span className="text-slate-400">— {n.by}</span></div>)}
      {history.map((h, i) => <div key={`h${i}`} className="text-slate-400">↳ {h.action?.replace(/_/g, " ")}{h.detail ? `: ${typeof h.detail === "object" ? Object.entries(h.detail).map(([k, v]) => `${k.replace(/_/g, " ")} → ${v}`).join(", ") : h.detail}` : ""} — {h.by}</div>)}
    </div>
  );
}

function EnrollTab({ list, selected, clearSelection, onChange, senders }) {
  const [mode, setMode] = useState("new");
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ new_name: "", sender_id: "", target_type: list.target_type, campaign_id: "" });
  const [busy, setBusy] = useState(false);
  const picked = Object.values(selected);

  useEffect(() => {
    req(`/campaigns/bespoke`).then((r) => r.json()).then((d) => setCampaigns(d.campaigns || [])).catch(() => {});
  }, []);

  const doEnroll = async () => {
    if (picked.length === 0) { toast.error("Select approved contacts first (tick them under Organisations)"); return; }
    const body = { contact_ids: picked.map((c) => c.target_contact_id) };
    if (mode === "existing") {
      if (!form.campaign_id) { toast.error("Choose a campaign"); return; }
      body.campaign_id = form.campaign_id;
    } else {
      if (!form.new_name || !form.sender_id) { toast.error("Campaign name and sender required"); return; }
      Object.assign(body, { new_name: form.new_name, sender_id: form.sender_id, target_type: form.target_type });
    }
    setBusy(true);
    try {
      const d = await req(`/target-lists/${list.target_list_id}/enroll`, { method: "POST", body: JSON.stringify(body) }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).detail || "Enroll failed");
        return r.json();
      });
      toast.success(`${d.enrolled} enrolled as draft recipients. Nothing sent.${d.skipped ? ` ${d.skipped} skipped (duplicate).` : ""}`);
      setForm({ new_name: "", sender_id: "", target_type: list.target_type, campaign_id: "" });
      clearSelection(); onChange();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="enroll-tab">
      <div className="text-sm text-slate-600">
        {picked.length} approved contact(s) selected. Enrolment creates draft recipients in a campaign and <strong>never sends</strong>.
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        {picked.map((c) => <Badge key={c.target_contact_id} variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">{c.name} · {c.company_name}</Badge>)}
      </div>
      <Tabs value={mode} onValueChange={setMode}>
        <TabsList>
          <TabsTrigger value="new" data-testid="enroll-mode-new">New campaign</TabsTrigger>
          <TabsTrigger value="existing" data-testid="enroll-mode-existing">Existing campaign</TabsTrigger>
        </TabsList>
        <TabsContent value="new" className="space-y-3 mt-3">
          <Input placeholder="Campaign name" value={form.new_name} onChange={(e) => setForm({ ...form, new_name: e.target.value })} data-testid="enroll-new-name" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.sender_id} onValueChange={(v) => setForm({ ...form, sender_id: v })}>
              <SelectTrigger data-testid="enroll-sender"><SelectValue placeholder="Sender" /></SelectTrigger>
              <SelectContent>{senders.map((s) => <SelectItem key={s.sender_id} value={s.sender_id}>{s.display_name} ({s.email})</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v })}>
              <SelectTrigger data-testid="enroll-target-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TARGET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </TabsContent>
        <TabsContent value="existing" className="space-y-3 mt-3">
          <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
            <SelectTrigger data-testid="enroll-campaign"><SelectValue placeholder="Choose a campaign" /></SelectTrigger>
            <SelectContent>{campaigns.map((c) => <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </TabsContent>
      </Tabs>
      <Button onClick={doEnroll} disabled={busy} data-testid="enroll-submit">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Enroll selected contacts
      </Button>
    </div>
  );
}

export default function TargetListsPanel() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);     // detailed list
  const [coverage, setCoverage] = useState(null);
  const [clients, setClients] = useState([]);
  const [senders, setSenders] = useState([]);
  const [selected, setSelected] = useState({});   // target_contact_id -> contact
  const [showCreate, setShowCreate] = useState(false);
  const [showAddOrgs, setShowAddOrgs] = useState(false);
  const [showSector, setShowSector] = useState(false);
  const [sectorForm, setSectorForm] = useState({ sector: "", country: "Nigeria", target_type: "executive", count: 25, name: "" });
  const [sectorBusy, setSectorBusy] = useState(false);
  const [newList, setNewList] = useState({ name: "", target_type: "executive", client_ids: [] });
  const [clientSearch, setClientSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const d = await req(`/target-lists`).then((r) => r.json());
      setLists(d.target_lists || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadLists();
    req(`/clients`).then((r) => r.json()).then((d) => setClients(Array.isArray(d) ? d : [])).catch(() => {});
    req(`/mission-control/senders`).then((r) => r.json()).then((d) => setSenders(d.senders || [])).catch(() => {});
  }, [loadLists]);

  const openList = useCallback(async (id) => {
    try {
      const [d, cov] = await Promise.all([
        req(`/target-lists/${id}`).then((r) => r.json()),
        req(`/target-lists/${id}/coverage`).then((r) => r.json()),
      ]);
      setActive(d.target_list); setCoverage(cov); setSelected({});
    } catch { toast.error("Could not open list"); }
  }, []);

  const refreshActive = useCallback(() => { if (active) openList(active.target_list_id); loadLists(); }, [active, openList, loadLists]);

  const createList = async () => {
    if (!newList.name || newList.client_ids.length === 0) { toast.error("Name and at least one organisation required"); return; }
    setBusy(true);
    try {
      const d = await req(`/target-lists`, { method: "POST", body: JSON.stringify(newList) }).then((r) => r.json());
      toast.success("Target list saved — organisations stored before any search");
      setShowCreate(false); setNewList({ name: "", target_type: "executive", client_ids: [] });
      await loadLists(); openList(d.target_list.target_list_id);
    } catch { toast.error("Could not create list"); }
    finally { setBusy(false); }
  };

  const addOrgs = async () => {
    if (newList.client_ids.length === 0) { toast.error("Select organisations"); return; }
    setBusy(true);
    try {
      await req(`/target-lists/${active.target_list_id}/orgs`, { method: "POST", body: JSON.stringify({ client_ids: newList.client_ids }) });
      toast.success("Organisations added"); setShowAddOrgs(false); setNewList({ ...newList, client_ids: [] }); refreshActive();
    } catch { toast.error("Could not add"); }
    finally { setBusy(false); }
  };

  const discoverAll = async () => {
    setBusy(true);
    try {
      const d = await req(`/target-lists/${active.target_list_id}/discover`, { method: "POST", body: "{}" }).then((r) => r.json());
      toast.success(`Searched ${d.searched} organisation(s)`); refreshActive();
    } catch { toast.error("Discovery failed"); }
    finally { setBusy(false); }
  };

  const discoverSector = async () => {
    const sector = sectorForm.sector.trim();
    const country = sectorForm.country.trim();
    if (!sector || !country) { toast.error("Enter a sector and a country"); return; }
    setSectorBusy(true);
    try {
      const r = await req(`/target-lists/discover-sector`, {
        method: "POST",
        body: JSON.stringify({ ...sectorForm, sector, country }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Discovery failed");
      const d = await r.json();
      toast.success(`${d.firms_found} firm(s) found and saved as prospects. Finding emails now…`);
      setShowSector(false);
      setSectorForm({ sector: "", country: "Nigeria", target_type: "executive", count: 25, name: "" });
      await loadLists();
      openList(d.target_list.target_list_id);
    } catch (err) { toast.error(err.message); }
    finally { setSectorBusy(false); }
  };

  // While a freshly-built sector list is still finding emails in the background,
  // poll so the UI fills in without a manual refresh.
  useEffect(() => {
    if (!active) return;
    const busyOrgs = (active.orgs || []).some((o) => ["pending", "searching"].includes(o.discovery_status));
    if (!busyOrgs) return;
    const t = setInterval(() => { openList(active.target_list_id); }, 6000);
    return () => clearInterval(t);
  }, [active, openList]);

  const removeList = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this target list and all its organisations?")) return;
    await req(`/target-lists/${id}`, { method: "DELETE" });
    toast.success("Deleted"); loadLists();
  };

  const toggleContact = (c) => setSelected((prev) => {
    const n = { ...prev };
    if (n[c.target_contact_id]) delete n[c.target_contact_id]; else n[c.target_contact_id] = c;
    return n;
  });

  const toggleClient = (cid) => setNewList((prev) => ({
    ...prev,
    client_ids: prev.client_ids.includes(cid) ? prev.client_ids.filter((x) => x !== cid) : [...prev.client_ids, cid],
  }));

  const filteredClients = clients.filter((c) => (c.company_name || "").toLowerCase().includes(clientSearch.toLowerCase()));

  // ─── Detail view ───
  if (active) {
    const pendingCount = (active.orgs || []).filter((o) => ["pending", "no_email_found", "search_failed"].includes(o.discovery_status) && !o.blocked).length;
    return (
      <div className="space-y-5" data-testid="target-list-detail">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setActive(null); setCoverage(null); }} data-testid="back-to-lists">
              <ChevronLeft className="w-4 h-4 mr-1" /> Lists
            </Button>
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">{active.name}</h2>
              <p className="text-xs text-slate-500">Target type: {active.target_type}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowAddOrgs(true)} data-testid="add-orgs-btn"><Plus className="w-4 h-4 mr-1" /> Add orgs</Button>
            <Button size="sm" onClick={discoverAll} disabled={busy || pendingCount === 0} data-testid="discover-all-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FolderSearch className="w-4 h-4 mr-1" />} Discover ({pendingCount})
            </Button>
          </div>
        </div>

        <CoverageBar coverage={coverage} />

        <Tabs defaultValue="orgs">
          <TabsList data-testid="detail-tabs">
            <TabsTrigger value="orgs" data-testid="tab-orgs"><Building2 className="w-4 h-4 mr-1" /> Organisations</TabsTrigger>
            <TabsTrigger value="investigation" data-testid="tab-investigation"><ClipboardList className="w-4 h-4 mr-1" /> Investigation</TabsTrigger>
            <TabsTrigger value="enroll" data-testid="tab-enroll"><Send className="w-4 h-4 mr-1" /> Enroll ({Object.keys(selected).length})</TabsTrigger>
          </TabsList>
          <TabsContent value="orgs" className="mt-4 space-y-2">
            {(active.orgs || []).map((org) => (
              <OrgRow key={org.target_org_id} list={active} org={org} onChange={refreshActive} onToggleContact={toggleContact} selected={selected} />
            ))}
            {(active.orgs || []).length === 0 && <div className="text-sm text-slate-500">No organisations yet. Use “Add orgs”.</div>}
          </TabsContent>
          <TabsContent value="investigation" className="mt-4">
            <InvestigationTab list={active} onChange={refreshActive} />
          </TabsContent>
          <TabsContent value="enroll" className="mt-4">
            <EnrollTab list={active} selected={selected} clearSelection={() => setSelected({})} onChange={refreshActive} senders={senders} />
          </TabsContent>
        </Tabs>

        <Dialog open={showAddOrgs} onOpenChange={setShowAddOrgs}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add organisations</DialogTitle></DialogHeader>
            <Input placeholder="Search…" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} className="mb-2" data-testid="addorg-search" />
            <div className="max-h-72 overflow-y-auto space-y-1">
              {filteredClients.slice(0, 100).map((c) => (
                <label key={c.client_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                  <Checkbox checked={newList.client_ids.includes(c.client_id)} onCheckedChange={() => toggleClient(c.client_id)} />
                  <span className="text-sm">{c.company_name}</span>
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={addOrgs} disabled={busy} data-testid="addorg-save">Add {newList.client_ids.length}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── List overview ───
  return (
    <div className="space-y-5" data-testid="target-lists-overview">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2"><Target className="w-5 h-5" /> Target lists</h2>
          <p className="text-sm text-slate-500">Persistent lists of target organisations — save first, discover contacts, approve, then enroll into a campaign.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowSector(true)} variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="discover-sector-btn">
            <Globe className="w-4 h-4 mr-1" /> Discover a new sector
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="new-list-btn"><Plus className="w-4 h-4 mr-1" /> New target list</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2 py-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : lists.length === 0 ? (
        <Card className="border-dashed border-slate-300"><CardContent className="py-10 text-center text-slate-500">
          <Target className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          No target lists yet. Create one to save organisations before searching for contacts.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lists.map((l) => {
            const oc = l.org_counts || {}; const cc = l.contact_counts || {};
            return (
              <Card key={l.target_list_id} className="border-slate-200 hover:border-slate-400 cursor-pointer transition-colors" onClick={() => openList(l.target_list_id)} data-testid={`list-card-${l.target_list_id}`}>
                <CardContent className="py-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="font-semibold text-slate-900">{l.name}</div>
                    <Button variant="ghost" size="sm" className="text-rose-500 -mt-1 -mr-2" onClick={(e) => removeList(l.target_list_id, e)} data-testid={`delete-list-${l.target_list_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Badge variant="outline" className="bg-slate-50">{l.target_type}</Badge>
                  <div className="flex gap-4 text-sm text-slate-600 pt-1">
                    <span><strong className="text-slate-900">{oc.total || 0}</strong> orgs</span>
                    <span className="text-emerald-600"><strong>{oc.ready_for_approval || 0}</strong> found</span>
                    <span className="text-amber-600"><strong>{(oc.no_email_found || 0) + (oc.search_failed || 0)}</strong> to chase</span>
                    <span className="text-violet-600"><strong>{cc.enrolled || 0}</strong> enrolled</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showSector} onOpenChange={setShowSector}>
        <DialogContent className="max-w-lg" data-testid="sector-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Globe className="w-5 h-5 text-indigo-600" /> Discover a new sector</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Rex web-researches real firms in a sector you've never contacted, saves them as watched
              <strong> prospects</strong> (not clients), then finds their emails. You review everything before anything sends.
            </p>
            <div>
              <Label>Sector / type of firm</Label>
              <Input value={sectorForm.sector} onChange={(e) => setSectorForm({ ...sectorForm, sector: e.target.value })} placeholder="e.g. commercial law firms" data-testid="sector-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Country</Label>
                <Input value={sectorForm.country} onChange={(e) => setSectorForm({ ...sectorForm, country: e.target.value })} placeholder="e.g. Nigeria" data-testid="sector-country" />
              </div>
              <div>
                <Label>Decision-makers</Label>
                <Select value={sectorForm.target_type} onValueChange={(v) => setSectorForm({ ...sectorForm, target_type: v })}>
                  <SelectTrigger data-testid="sector-target-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TARGET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>How many firms</Label>
              <Select value={String(sectorForm.count)} onValueChange={(v) => setSectorForm({ ...sectorForm, count: Number(v) })}>
                <SelectTrigger data-testid="sector-count"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">About 25</SelectItem>
                  <SelectItem value="50">About 50</SelectItem>
                  <SelectItem value="100">About 100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>List name (optional)</Label>
              <Input value={sectorForm.name} onChange={(e) => setSectorForm({ ...sectorForm, name: e.target.value })} placeholder="Auto: “Commercial Law Firms — Nigeria”" data-testid="sector-name" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={discoverSector} disabled={sectorBusy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="sector-submit">
              {sectorBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Researching firms…</> : <><Sparkles className="w-4 h-4 mr-2" /> Build the list</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New target list</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>List name</Label>
              <Input value={newList.name} onChange={(e) => setNewList({ ...newList, name: e.target.value })} placeholder="e.g. Q3 Banking executives" data-testid="create-name" />
            </div>
            <div>
              <Label>Target type</Label>
              <Select value={newList.target_type} onValueChange={(v) => setNewList({ ...newList, target_type: v })}>
                <SelectTrigger data-testid="create-target-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TARGET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Organisations ({newList.client_ids.length} selected)</Label>
              <Input placeholder="Search…" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} className="my-1" data-testid="create-client-search" />
              <div className="max-h-56 overflow-y-auto space-y-1 border border-slate-200 rounded p-1">
                {filteredClients.slice(0, 100).map((c) => (
                  <label key={c.client_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                    <Checkbox checked={newList.client_ids.includes(c.client_id)} onCheckedChange={() => toggleClient(c.client_id)} data-testid={`create-client-${c.client_id}`} />
                    <span className="text-sm">{c.company_name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={createList} disabled={busy} data-testid="create-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save list ({newList.client_ids.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
